import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, MessageCircle } from "lucide-react";
import { BUSINESS } from "@/data";
import { Magnetic } from "@/components/site/motion-lib";

const NAV = [
  { label: "Servizi", href: "#servizi" },
  { label: "Lavori", href: "#lavori" },
  { label: "Chi siamo", href: "#chi-siamo" },
  { label: "Zona", href: "#zona" },
  { label: "Contatti", href: "#contatti" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-[100] transition-[background-color,backdrop-filter,padding,border-color] duration-500 border-b ${
        scrolled
          ? "bg-[#141414]/80 backdrop-blur-xl border-[#F5F1EA]/10 py-3"
          : "bg-transparent border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between gap-4">
        <a href="#top" data-testid="logo-link" className="flex flex-col leading-none group">
          <span className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-[#F5F1EA]">
            Officine <span className="text-[#C89B4A]">Galloni</span>
          </span>
          <span className="text-[0.6rem] tracking-[0.35em] uppercase text-[#F5F1EA]/50 mt-1">
            dal 1979
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Navigazione principale">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.href.slice(1)}`}
              className="text-sm text-[#F5F1EA]/75 hover:text-[#C89B4A] transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#C89B4A] hover:after:w-full after:transition-all after:duration-300"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-phone"
            className="flex items-center gap-2 text-sm text-[#F5F1EA] hover:text-[#C89B4A] transition-colors"
          >
            <Phone size={16} strokeWidth={1.5} className="text-[#C89B4A]" />
            <span className="font-medium">{BUSINESS.phone}</span>
          </a>
          <Magnetic>
            <a
              href="#contatti"
              data-testid="header-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#C89B4A] px-5 py-2.5 text-sm font-semibold text-[#141414] hover:bg-[#d8ab5a] transition-colors duration-300"
            >
              Preventivo gratuito
            </a>
          </Magnetic>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Apri menu"
          className="lg:hidden text-[#F5F1EA] p-2"
        >
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#1c1c1c]/95 backdrop-blur-xl border-t border-[#F5F1EA]/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-lg text-[#F5F1EA]/85 hover:text-[#C89B4A] transition-colors"
                >
                  {n.label}
                </a>
              ))}
              <div className="flex gap-3 pt-3">
                <a href={BUSINESS.phoneHref} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#C89B4A]/50 px-4 py-3 text-sm text-[#C89B4A]">
                  <Phone size={16} strokeWidth={1.5} /> Chiama
                </a>
                <a href={BUSINESS.whatsapp} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#C89B4A] px-4 py-3 text-sm font-semibold text-[#141414]">
                  <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
